// import components
import { Button, CircularProgress, Typography } from '@mui/material';
import { ToolbarWrapper } from 'components/shared/ToolbarSet/ToolbarWrapper';
import BacklogCategoryTiles from './components/BacklogCategoryTiles';
import BacklogGrid from './components/BacklogGrid';
import BacklogToolbarControls from './components/BacklogToolbarControls';
import BacklogWeekDetail from './components/BacklogWeekDetail';

// import external libraries
import { useTranslation } from 'react-i18next';

// import hooks
import { useBacklog } from './Backlog.hooks';

// import utils
import { getWeekDays } from './components/BacklogGrid/BacklogGrid.utils';

// import styles
import {
  BacklogContentArea,
  BacklogPageContainer,
  BacklogStateArea,
  BacklogTilesArea,
  BacklogToolbarArea
} from './Backlog.styles';

const Backlog = () => {
  const { t } = useTranslation('common', { keyPrefix: 'backlog' });

  const {
    selectedDate,
    onDateChange,
    activityOptions,
    selectedActivityOptions,
    onActivityCodesChange,
    careReceiverOptions,
    selectedCareReceiverOptions,
    onCareReceiverIdsChange,
    categoryCounts,
    activeCategories,
    onToggleCategory,
    onToggleAll,
    onToggleCategoryRow,
    cellValueMode,
    setCellValueMode,
    showTotals,
    setShowTotals,
    showCompleted,
    setShowCompleted,
    rosterDays,
    rows,
    backlog,
    weeks,
    ruleRows,
    filters,
    collapsedCategoryKeys,
    openWeek,
    onOpenWeek,
    onCloseWeek,
    onPeriodChange,
    isLoading,
    isError,
    refetch
  } = useBacklog();

  const renderContent = () => {
    if (isLoading) {
      return (
        <BacklogStateArea>
          <CircularProgress />
        </BacklogStateArea>
      );
    }

    if (isError) {
      return (
        <BacklogStateArea>
          <Typography variant="body2" color="text.secondary">
            {t('loadError')}
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            {t('retry')}
          </Button>
        </BacklogStateArea>
      );
    }

    // The week-detail is a drill-down of the same data, so it takes over the content area when a week
    // is open — its own back breadcrumb returns to the weeks grid.
    if (openWeek) {
      return (
        <BacklogWeekDetail
          weekDays={getWeekDays(weeks, openWeek)}
          weekField={openWeek}
          backlog={backlog}
          ruleRows={ruleRows}
          filters={filters}
          activeCategories={activeCategories}
          cellValueMode={cellValueMode}
          collapsedCategoryKeys={collapsedCategoryKeys}
          onToggleCategoryRow={onToggleCategoryRow}
          onClose={onCloseWeek}
        />
      );
    }

    if (activeCategories.length === 0) {
      return (
        <BacklogStateArea>
          <Typography variant="body2" color="text.secondary">
            {t('noCategorySelected')}
          </Typography>
        </BacklogStateArea>
      );
    }

    if (rows.length === 0) {
      return (
        <BacklogStateArea>
          <Typography variant="body2" color="text.secondary">
            {t('noDeviations')}
          </Typography>
        </BacklogStateArea>
      );
    }

    return (
      <BacklogGrid
        rows={rows}
        rosterDays={rosterDays}
        cellValueMode={cellValueMode}
        showTotals={showTotals}
        onToggleCategoryRow={onToggleCategoryRow}
        onOpenWeek={onOpenWeek}
      />
    );
  };

  return (
    <BacklogPageContainer>
      <BacklogTilesArea>
        <BacklogCategoryTiles
          categoryCounts={categoryCounts}
          activeCategories={activeCategories}
          onToggleCategory={onToggleCategory}
          onToggleAll={onToggleAll}
        />
      </BacklogTilesArea>

      <BacklogToolbarArea>
        <ToolbarWrapper
          // Default GRID flow: both toolbar controls sit in the left area (before the date picker), the
          // date picker keeps the centre column, and the right area is unused.
          dateNavigation={{
            showDateNavigation: true,
            defaultPeriod: 'eightWeeks',
            selectedDate,
            onSelectedDate: onDateChange,
            // The 8-weken/week choice lives in the date-picker menu, so it stays reachable when the
            // toolbar is docked to the right. Picking 'week' opens the week-detail; 'eightWeeks' the grid.
            dateNavigationOptions: ['eightWeeks', 'week'],
            allowDateNavigationMenuOptions: true,
            onPeriodChange,
            // The eightWeeks/week switch is this view's own drill-down, not an app-wide preference, so it
            // must not write `defaultDateNavigationPeriod` (which would leak 'week' into the roster and
            // other date-navigated views, and reopen the takenlijst straight into the week-detail).
            persistPeriodPreference: false,
            // The period name would read "8 weken"/"Week" before the date; the menu already says which,
            // so only the date itself is shown.
            hidePeriodDescription: true
          }}
          component={{
            // Both toolbar controls sit on the left, before the date picker: a filters (sliders) icon
            // and an eye icon, each opening its settings in a popper on press.
            beforeDatePicker: (
              <BacklogToolbarControls
                activityOptions={activityOptions}
                selectedActivityOptions={selectedActivityOptions}
                onActivityCodesChange={onActivityCodesChange}
                careReceiverOptions={careReceiverOptions}
                selectedCareReceiverOptions={selectedCareReceiverOptions}
                onCareReceiverIdsChange={onCareReceiverIdsChange}
                cellValueMode={cellValueMode}
                onCellValueModeChange={setCellValueMode}
                showTotals={showTotals}
                onShowTotalsChange={setShowTotals}
                showCompleted={showCompleted}
                onShowCompletedChange={setShowCompleted}
              />
            )
          }}
        >
          <BacklogContentArea>{renderContent()}</BacklogContentArea>
        </ToolbarWrapper>
      </BacklogToolbarArea>
    </BacklogPageContainer>
  );
};

export default Backlog;
